import React from 'react';
import { Flex, Box } from 'rebass';
import Slider from 'react-slick';
import { Controller, Scene } from 'react-scrollmagic';

import CarouselSlide from '../../components/CarouselSlide/CarouselSlide';
import FeaturedCompanySlide from '../../components/FeaturedCompanySlide/FeaturedCompanySlide';
import NarrowContainer from '../../components/NarrowContainer/NarrowContainer';
import Button from '../../components/Button/Button';
import CarouselChevron from '../../components/CarouselChevron/CarouselChevron';
import Typography, {
  TypographyContextProvider,
} from '../../components/Typography/Typography';

import colourMatching from '../../utils/colourMatching';
import { theme } from '../../styles/theme';
import CarouselStyles from '../../styles/CarouselStyles';
import isFadeEnabled from '../../utils/isFadeEnabled';
import { parsePadding } from '../../utils/parseSpacing';
import breakpointConverter from '../../utils/breakpointConverter';
import { CarouselSectionStoryblok } from '../../types/storyblok';

const CarouselSection = ({
  title,
  slides,
  ctaText,
  ctaUrl,
  stackContent,
  colourScheme,
  slidesToShow,
  slidesToScroll,
  paddingTop,
  paddingBottom,
}: CarouselSectionStoryblok) => {
  const slider = React.useRef();
  const nextSlide = () => slider.current.slickNext();
  const prevSlide = () => slider.current.slickPrev();

  let responsive = [
    {
      breakpoint: breakpointConverter(theme.breakpoint.md),
      settings: {
        dots: true,
      },
    },
  ];
  if (parseInt(slidesToShow) > 1) {
    responsive = [
      {
        breakpoint: parseInt(theme.breakpoint.xlg),
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
          dots: true,
        },
      },
      {
        breakpoint: parseInt(theme.breakpoint.lg),
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
          dots: true,
        },
      },
      {
        breakpoint: parseInt(theme.breakpoint.md),
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          dots: true,
        },
      },
    ];
  }
  const sliderSettings = {
    dots: false,
    infinite: true,
    arrows: false,
    slidesToShow: parseInt(slidesToShow) || 1,
    slidesToScroll: parseInt(slidesToScroll) || 1,
    responsive,
  };

  const paddings = {
    ...(paddingTop && { pt: parsePadding(paddingTop).pt }),
    ...(paddingBottom && { pb: parsePadding(paddingBottom).pb }),
  };

  const carouselComponent = () =>
    slides.length > 0 ? (
      slides.map((slide) => {
        return slide.component === 'CarouselSlide' ? (
          <CarouselSlide
            key={slide._uid}
            {...slide}
            colourScheme={colourScheme}
          />
        ) : (
          <FeaturedCompanySlide
            key={slide._uid}
            {...slide}
            colourScheme={colourScheme}
          />
        );
      })
    ) : (
      <Typography textAlign="center">No slides added</Typography>
    );

  return (
    <TypographyContextProvider value={{ colourScheme }}>
      <Box
        as="section"
        backgroundColor={colourMatching(colourScheme)}
        sx={{
          px: [3, 4],
          py: [8, 9, 10],
          ...paddings,
        }}
      >
        <Controller globalSceneOptions={{ triggerHook: 'onEnter' }}>
          <Scene classToggle="full-opacity">
            <NarrowContainer className={isFadeEnabled() ? 'zero-opacity' : ''}>
              <Flex flexWrap="wrap">
                {title && (
                  <Box width={1} px={3}>
                    <Typography
                      component="h2"
                      mb={[3, 4, 7]}
                      textAlign="center"
                      mx="auto"
                    >
                      {title}
                    </Typography>
                  </Box>
                )}
                <Box width={1} px={[3, 4, 5]} sx={{ position: 'relative' }}>
                  <CarouselStyles colourScheme={colourScheme}>
                    {stackContent ? (
                      <>{carouselComponent()}</>
                    ) : (
                      <Slider ref={slider} {...sliderSettings}>
                        {carouselComponent()}
                      </Slider>
                    )}
                  </CarouselStyles>
                  {slides.length > 1 && (
                    <Box>
                      <CarouselChevron
                        left
                        onClick={prevSlide}
                        p={3}
                        mx={-3}
                        colourScheme={colourScheme}
                      />
                      <CarouselChevron
                        right
                        onClick={nextSlide}
                        p={3}
                        mx={-3}
                        colourScheme={colourScheme}
                      />
                    </Box>
                  )}
                </Box>
                {ctaText && (
                  <Box width={1} textAlign="center" px={3} pt={[8, 9, 8]}>
                    <Button href={ctaUrl} colourScheme={colourScheme} arrow>
                      {ctaText}
                    </Button>
                  </Box>
                )}
              </Flex>
            </NarrowContainer>
          </Scene>
        </Controller>
      </Box>
    </TypographyContextProvider>
  );
};

export default CarouselSection;
